/* Primary-nav rail. Platform modules at top, plus a pinned-bottom group
   (Settings / Support). Mirrors andavo-dev's SharedWebNav side rail.

   RESPONSIVE: on desktop (≥ lg) it's a fixed in-flow rail. On small viewports it
   is an off-canvas DRAWER — fixed-position, slides in/out via the `open` prop
   (driven by AppShell's hamburger), with a close button. Prop-driven so every
   surface supplies its own nav model. `defaultPlatformNav` / `defaultPinnedNav`
   are exported for the common case (Chat / CRM / Expense + Settings / Support).

   WIDTH: the desktop rail was a fixed 240px (`lg:w-60`), which clipped the longest
   label in the platform IA — "Hotel Supplier Management" rendered as "Hotel
   Supplier Manage…" because SideNavItem's label is overflow-hidden. It now defaults
   to 272px (fits that label) and is USER-RESIZABLE via a drag handle on its right
   edge, persisted per browser (useRailWidth). The handle is desktop-only: below lg
   the rail is a swipe-open drawer, where a resize grip means nothing. */
import {
  ChatCircleIcon,
  GearSixIcon,
  QuestionIcon,
  ReceiptIcon,
  UsersThreeIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Fragment } from 'react';
import { matchPath, useLocation } from 'react-router';
import type { NavItem } from '../types';
import { cn } from '../util/cn';
import { SideNavItem } from './SideNavItem';
import { RAIL_WIDTH_MAX, RAIL_WIDTH_MIN, useRailWidth } from './useRailWidth';

/** The platform IA: top-level modules in the primary rail. */
export const defaultPlatformNav: NavItem[] = [
  { icon: <ChatCircleIcon />, label: 'Chat', path: '/chat' },
  { icon: <UsersThreeIcon />, label: 'Customer Management', path: '/crm' },
  { icon: <ReceiptIcon />, label: 'Expense', path: '/expense' },
];

/** Pinned-bottom utility entries. */
export const defaultPinnedNav: NavItem[] = [
  { icon: <GearSixIcon />, label: 'Settings', path: '/settings' },
  { icon: <QuestionIcon />, label: 'Support', path: '/support' },
];

export type SideNavProps = {
  /** Primary-nav (Platform) entries shown at the top of the rail. */
  items?: NavItem[];
  /** Pinned-bottom entries (Settings / Support, etc.). */
  pinnedItems?: NavItem[];
  /** Mobile drawer open state (ignored on desktop, where the rail is static). */
  open?: boolean;
  /** Close the mobile drawer (e.g. the in-drawer close button). */
  onClose?: () => void;
};

export function SideNav({
  items = defaultPlatformNav,
  pinnedItems = defaultPinnedNav,
  open = false,
  onClose,
}: SideNavProps) {
  const rail = useRailWidth();
  const location = useLocation();
  const isActive = (path: string) =>
    !!matchPath(`${path}/*`, location.pathname) || location.pathname === path;

  return (
    <nav
      id="main-navigation"
      aria-hidden={
        // On mobile, the drawer is inert (off-canvas) when closed. On desktop
        // it's always the visible rail. (Tailwind can't set aria-hidden
        // responsively, so this reflects the mobile state; desktop ignores it
        // via the lg: classes that keep it on-screen + interactive.)
        undefined
      }
      data-open={open ? 'true' : 'false'}
      data-rail-width={rail.width}
      // The chosen width rides in as a CSS custom property so the responsive
      // `lg:w-[…]` class can consume it — Tailwind can't emit a dynamic width, and
      // a plain inline `width` would override the mobile drawer's w-72 too.
      style={{ '--rail-w': `${rail.width}px` } as React.CSSProperties}
      className={cn(
        // Mobile: off-canvas fixed drawer below the 56px top bar; slides via open.
        // `overflow-y-auto` so a long nav (e.g. role-gated extras) scrolls inside
        // the drawer instead of overflowing the viewport; `pb` adds the iOS home-
        // indicator safe-area inset (viewport-fit=cover lets the drawer reach the
        // physical screen edge). `max-w-[85vw]` keeps the 288px drawer from
        // covering the whole screen on the narrowest phones, preserving a scrim
        // strip to tap-to-close.
        'fixed top-14 bottom-0 left-0 z-40 flex w-72 max-w-[85vw] flex-col overflow-y-auto overscroll-contain border-r border-border-subtle bg-surface-midground py-4 pb-[max(1rem,env(safe-area-inset-bottom))] transition-transform duration-200',
        open ? 'translate-x-0' : '-translate-x-full',
        // Desktop (≥ lg): a normal in-flow fixed-width rail, always on-screen.
        // `lg:top-auto lg:bottom-auto` is LOAD-BEARING, not tidying. The mobile
        // drawer is `fixed top-14 bottom-0`; a static element ignores those
        // offsets, but `relative` HONOURS them — so switching to lg:relative (to
        // anchor the resize handle) silently pushed the desktop rail 56px down,
        // leaving a gap under the top bar. Reset the insets at lg and the rail
        // sits flush again while still being a positioned ancestor.
        'lg:relative lg:top-auto lg:bottom-auto lg:z-auto lg:w-[var(--rail-w)] lg:max-w-none lg:translate-x-0 lg:pb-4',
      )}
    >
      {/* Mobile-only drawer header with a close button (hidden on desktop). */}
      <div className="flex items-center justify-between px-4 pb-2 lg:hidden">
        <span className="font-inter font-semibold text-content-secondary text-xxs uppercase tracking-wider">
          Menu
        </span>
        <button
          type="button"
          aria-label="Close navigation menu"
          data-testid="close-nav"
          onClick={onClose}
          className="flex size-11 items-center justify-center rounded-md text-content-secondary hover:bg-surface-foreground"
        >
          <XIcon className="size-icon-sm" />
        </button>
      </div>

      <ul className="flex flex-col gap-1 px-3">
        <li className="px-4 pb-1 font-inter font-semibold text-content-secondary text-xxs uppercase tracking-wider">
          Platform
        </li>
        {items.map((item) => {
          const pathStr = String(item.path);
          const sectionActive = isActive(pathStr);
          const hasChildren = !!item.children?.length;
          return (
            <Fragment key={pathStr}>
              <SideNavItem
                icon={item.icon}
                label={item.label}
                path={item.path}
                // A parent WITH children takes the dark pill only on an exact
                // match; while inside the section the active CHILD carries it and
                // the parent just expands. Leaf items keep the section-match pill.
                isActive={
                  hasChildren ? location.pathname === pathStr : sectionActive
                }
              />
              {hasChildren &&
                sectionActive &&
                item.children?.map((child) => (
                  <SideNavItem
                    key={String(child.path)}
                    icon={child.icon}
                    label={child.label}
                    path={child.path}
                    isActive={isActive(String(child.path))}
                    nested
                  />
                ))}
            </Fragment>
          );
        })}
      </ul>

      <RailResizeHandle rail={rail} />

      {pinnedItems.length > 0 && (
        <ul className="mt-auto flex flex-col gap-1 border-t border-border-subtle px-3 pt-4">
          {pinnedItems.map((item) => (
            <SideNavItem
              key={String(item.path)}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isActive={isActive(String(item.path))}
            />
          ))}
        </ul>
      )}
    </nav>
  );
}

/**
 * The drag grip on the rail's right edge.
 *
 * DESKTOP ONLY (`hidden lg:block`): below `lg` the rail is a swipe-open drawer, so
 * a resize grip there would be a control that does nothing.
 *
 * It is a real `separator` with aria-valuenow/min/max and full keyboard support
 * (arrows nudge, Home/End jump to the limits, Enter/Space resets) — this is primary
 * navigation, and a drag-only affordance would put its width out of reach for
 * anyone not using a mouse. The hit area is deliberately wider than the 1px visual
 * line: a 4px-wide target is a precision test, not an affordance.
 */
function RailResizeHandle({ rail }: { rail: ReturnType<typeof useRailWidth> }) {
  return (
    <div
      aria-label="Resize navigation. Arrow keys adjust, Enter resets."
      aria-orientation="vertical"
      aria-valuemax={RAIL_WIDTH_MAX}
      aria-valuemin={RAIL_WIDTH_MIN}
      aria-valuenow={rail.width}
      className={cn(
        // Full-height strip on the right edge, straddling the border so the grab
        // area covers both sides of the visual line.
        'absolute top-0 right-0 bottom-0 hidden w-2 translate-x-1/2 cursor-col-resize lg:block',
        'focus-visible:outline-none',
        // The line only shows on hover/focus/drag — a permanent divider would add
        // chrome to a rail that already has a border.
        'after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent after:transition-colors',
        'hover:after:bg-border-action focus-visible:after:bg-border-action',
        rail.isDragging && 'after:bg-border-action',
      )}
      data-testid="rail-resize-handle"
      onDoubleClick={rail.reset}
      onKeyDown={rail.onKeyDown}
      onPointerDown={rail.onPointerDown}
      role="separator"
      tabIndex={0}
    />
  );
}
